import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceRecorderState = 'idle' | 'recording' | 'paused' | 'stopped';

const WEBM_OPUS = 'audio/webm;codecs=opus';
const WEBM = 'audio/webm';

function pickWebmMimeType(): string {
  if (MediaRecorder.isTypeSupported(WEBM_OPUS)) return WEBM_OPUS;
  if (MediaRecorder.isTypeSupported(WEBM)) return WEBM;
  return '';
}

export function useVoiceRecorder() {
  const [state, setState] = useState<VoiceRecorderState>('idle');
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pauseStartedRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearTimer();
      stopStream();
      if (mediaRecorderRef.current?.state !== 'inactive') {
        try {
          mediaRecorderRef.current?.stop();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const tickDuration = useCallback(() => {
    const pausedExtra =
      pauseStartedRef.current != null
        ? Date.now() - pauseStartedRef.current
        : 0;
    setDurationMs(
      Date.now() - startedAtRef.current - pausedAccumRef.current - pausedExtra
    );
  }, []);

  const start = useCallback(async () => {
    setError(null);
    chunksRef.current = [];
    blobRef.current = null;
    pausedAccumRef.current = 0;
    pauseStartedRef.current = null;

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Voice recording is not supported on this device.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickWebmMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || WEBM;
        blobRef.current = new Blob(chunksRef.current, { type });
        stopStream();
        clearTimer();
        setState('stopped');
      };

      recorder.start(200);
      startedAtRef.current = Date.now();
      setDurationMs(0);
      setState('recording');
      clearTimer();
      timerRef.current = window.setInterval(tickDuration, 200);
    } catch {
      setError('Microphone permission is required to record voice notes.');
      stopStream();
      setState('idle');
    }
  }, [tickDuration]);

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
    pauseStartedRef.current = Date.now();
    clearTimer();
    setState('paused');
  }, []);

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    if (pauseStartedRef.current != null) {
      pausedAccumRef.current += Date.now() - pauseStartedRef.current;
      pauseStartedRef.current = null;
    }
    recorder.resume();
    timerRef.current = window.setInterval(tickDuration, 200);
    setState('recording');
  }, [tickDuration]);

  const stop = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    if (pauseStartedRef.current != null) {
      pausedAccumRef.current += Date.now() - pauseStartedRef.current;
      pauseStartedRef.current = null;
    }
    recorder.stop();
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    stopStream();
    chunksRef.current = [];
    blobRef.current = null;
    mediaRecorderRef.current = null;
    pausedAccumRef.current = 0;
    pauseStartedRef.current = null;
    setDurationMs(0);
    setState('idle');
    setError(null);
  }, []);

  const getBlob = useCallback(() => blobRef.current, []);

  const getFile = useCallback((): File | null => {
    const blob = blobRef.current;
    if (!blob) return null;
    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    return new File([blob], `voice-note-${Date.now()}.${ext}`, {
      type: blob.type || WEBM,
    });
  }, []);

  return {
    state,
    durationMs,
    error,
    start,
    pause,
    resume,
    stop,
    reset,
    getBlob,
    getFile,
    isActive: state === 'recording' || state === 'paused',
  };
}
